"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircleQuestionMark as HelpCircle, Mail, Minus, Plus, ShieldCheck, Truck, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqData = [{
  id: "orders-tracking",
  category: "LOGISTICS",
  question: "How do I track my order once it ships?",
  answer: "Once your order is processed and packaged at our facility, you will receive an email confirmation containing an automated tracking number and freight carrier details. You can track parcel deliveries in real time or monitor freight line-haul dispatch directly via the carrier portal.",
  code: "SPEC // DISPATCH-24H"
}, {
  id: "shipping-freight",
  category: "SHIPPING",
  question: "What are your standard delivery times and freight methods?",
  answer: "Standard shop tools and parcel shipments ship within 1 to 2 business days and arrive within 3 to 6 business days across the continental U.S. Heavy machinery, hydraulic lifts, and oversized shop hardware ship via dedicated freight carrier with hydraulic liftgate service included at delivery.",
  code: "SPEC // LTL-FREIGHT"
}, {
  id: "warranty-returns",
  category: "WARRANTY",
  question: "What is your warranty and 30-day return policy?",
  answer: "All tools and shop equipment carry full manufacturer warranty coverage against mechanical defects and material failure. Unused items in original factory packaging can be returned within 30 days of delivery. Our customer support team issues return merchandise authorization (RMA) within 24 hours.",
  code: "SPEC // RMA-30DAY"
}, {
  id: "tool-specs",
  category: "TECHNICAL",
  question: "How do I verify duty-cycle ratings and technical specifications?",
  answer: "Every product listing includes a dedicated specification sheet covering motor horsepower, electrical voltage requirements, pneumatic CFM consumption, and dimensional tolerances. If your fabrication project requires specific engineering data, our technical team provides direct mechanical documentation.",
  code: "SPEC // ISO-METRIC"
}, {
  id: "payment-security",
  category: "CHECKOUT",
  question: "Which payment methods and business terms are accepted?",
  answer: "We accept all major credit and debit cards, PayPal, Apple Pay, and Google Pay through our encrypted, PCI-compliant gateway. For commercial workshops and fleet operations placing high-volume orders, corporate invoicing and purchase orders are available upon credit approval.",
  code: "SPEC // 256-BIT-SSL"
}];
export default function Faq() {
  const [openItem, setOpenItem] = useState<string | null>("orders-tracking");
  const toggleItem = (id: string) => {
    setOpenItem(prev => prev === id ? null : id);
  };
  return <section id="frequently-asked-questions" data-nav="dark" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Header with Precision Spec Rule */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.24,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-2 w-2 bg-primary rounded-none" />
            <span className="font-mono text-xs md:text-sm font-semibold tracking-wider text-muted-foreground uppercase">
              Operational Handbook // FAQ-01
            </span>
          </div>

          <div className="border-l-3 border-primary pl-4 md:pl-6">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground">
              Frequently asked questions
            </h2>
            <p className="mt-3 text-base md:text-lg text-muted-foreground font-sans max-w-2xl">
              Clear technical details on orders, nationwide freight logistics, warranties, and workshop hardware specifications.
            </p>
          </div>
        </motion.div>

        {/* FAQ Accordion List */}
        <div className="space-y-3">
          {faqData.map((item, i) => {
          const isOpen = openItem === item.id;
          return <motion.div key={item.id} data-index={i} initial={{
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.24,
            delay: i * 0.05,
            ease: [0.16, 1, 0.3, 1]
          }} className="rounded-sm border border-border bg-card overflow-hidden transition-[border-color,box-shadow] duration-150 hover:border-primary/50">
                <Button type="button" variant="ghost" onClick={() => toggleItem(item.id)} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background h-auto font-normal hover:bg-transparent rounded-none">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 flex-1">
                    <span className="font-mono text-xs font-bold text-primary tracking-wide">
                      [{item.category}]
                    </span>
                    <span className="font-sans text-base md:text-lg font-semibold text-card-foreground">
                      {item.question}
                    </span>
                  </div>
                  <div className="shrink-0 size-8 rounded-sm bg-muted flex items-center justify-center text-primary border border-border transition-colors duration-150">
                    {isOpen ? <Minus className="size-4 stroke-[2.5]" /> : <Plus className="size-4 stroke-[2.5]" />}
                  </div>
                </Button>

                <AnimatePresence initial={false}>
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: "auto",
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease: [0.16, 1, 0.3, 1]
              }} className="overflow-hidden">
                      <div className="px-5 pb-6 md:px-6 pt-1 border-t border-border/40">
                        <p className="font-sans text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
                          {item.answer}
                        </p>
                        <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-border/20">
                          <span className="font-mono text-xs text-muted-foreground/80">
                            {item.code}
                          </span>
                          <span className="font-mono text-xs text-primary/80">
                            VERIFIED STANDARD
                          </span>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>

        {/* Technical Help Card Footer */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.24,
        delay: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-12 rounded-sm border border-border bg-card/60 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Wrench className="size-5 text-primary" />
              <h3 className="font-serif text-xl font-bold uppercase tracking-tight text-foreground">
                Need custom workshop specifications?
              </h3>
            </div>
            <p className="text-sm text-muted-foreground font-sans max-w-xl">
              Our technical support team can answer questions about load ratings, air pressure requirements, or shipping logistics.
            </p>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <Link to="/ContactPage" className="w-full md:w-auto">
              <Button variant="default" className="w-full md:w-auto font-serif uppercase tracking-wider font-bold h-11 px-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                <Mail className="size-4 mr-2" />
                Contact team
              </Button>
            </Link>
            <Link to="/ShippingReturnsPage" className="w-full md:w-auto">
              <Button variant="outline" className="w-full md:w-auto font-serif uppercase tracking-wider font-bold h-11 px-6 rounded-sm border-border text-foreground hover:bg-muted transition-colors">
                <Truck className="size-4 mr-2" />
                Shipping terms
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>;
}